import { EnvironmentalData } from "./environmentalDataService";

const PREDICTION_API_URL =
  import.meta.env.VITE_PREDICTION_API_URL ||
  (typeof window !== "undefined"
    ? "/prediction-proxy"
    : "http://127.0.0.1:8001");

export interface PredictionResponse {
  predictions: {
    deforestation_rate_5y: number;
    desertification_area_5y: number;
    species_impact_5y: number;
    land_loss_5y: number;
    deforestation_rate_10y?: number;
    desertification_area_10y?: number;
    species_impact_10y?: number;
    land_loss_10y?: number;
    timeframe: "5y" | "10y";
    confidence: {
      deforestation: number;
      desertification: number;
      species: number;
      land_loss: number;
    };
  };
  region_id: string;
  timestamp: string;
  // Enhanced server info for debugging
  model_type?: string;
  accuracy_note?: string;
  confidence_level?: string;
  server_info?: {
    version: string;
    dependencies: string[];
    model_loaded: boolean;
  };
}

export async function generatePrediction(
  regionId: string,
  data: EnvironmentalData,
  timeframe: "5y" | "10y" = "10y",
  userId?: string,
): Promise<PredictionResponse> {
  try {
    console.log(
      "Generating prediction for region:",
      regionId,
      "with data:",
      data,
      "timeframe:",
      timeframe,
    );
    console.log("Land loss data being sent:", {
      ratePerYear: data.landLoss.ratePerYear,
      totalLost: data.landLoss.totalLost,
      deforestation_ratePerYear: data.deforestation.ratePerYear,
    });

    // Use proxy path in production (avoids CORS); env var overrides for local dev
    const apiUrl =
      import.meta.env.VITE_PREDICTION_API_URL || "/prediction-proxy";
    console.log("🔍 Using prediction API URL:", apiUrl);

    const body: any = {
      region_id: regionId,
      timeframe: timeframe,
      deforestation_rate_per_year: data.deforestation.ratePerYear,
      deforestation_total_lost: data.deforestation.totalLost,
      land_loss_rate_per_year: data.landLoss.ratePerYear,
      land_loss_total_lost: data.landLoss.totalLost,
      desertification_rate_per_year: data.desertification.ratePerYear,
      desertification_affected_area: data.desertification.affectedArea,
      endangered_species_count: data.endangeredSpecies.count,
      endangered_species_critically_endangered:
        data.endangeredSpecies.criticallyEndangered,
      endangered_species_endangered: data.endangeredSpecies.endangered,
      endangered_species_vulnerable: data.endangeredSpecies.vulnerable,
    };

    if (userId) body.user_id = userId;

    const response = await fetch(`${apiUrl}/api/prediction`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("🚀 Prediction response:", result);

    // Log server type information for debugging
    if (result.model_type) {
      console.log(`📊 Server Type: ${result.model_type}`);
      console.log(`🎯 Accuracy: ${result.accuracy_note}`);
      console.log(`📈 Confidence: ${result.confidence_level}`);
    }

    return result;
  } catch (error) {
    console.error("Error generating prediction:", error);
    throw error;
  }
}

// Health check function to test which server is running
export async function checkServerHealth(): Promise<{
  status: "healthy" | "error";
  server_type: string;
  version?: string;
  model_loaded?: boolean;
  dependencies?: string[];
  url: string;
}> {
  try {
    const apiUrl =
      import.meta.env.VITE_PREDICTION_API_URL || "/prediction-proxy";
    console.log("🔍 Checking server health at:", apiUrl);

    const response = await fetch(`${apiUrl}/health`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("🏥 Server health response:", result);

    return {
      status: "healthy",
      server_type: result.server_type || "unknown",
      version: result.version,
      model_loaded: result.model_loaded,
      dependencies: result.dependencies,
      url: apiUrl,
    };
  } catch (error) {
    console.error("❌ Server health check failed:", error);
    return {
      status: "error",
      server_type: "unavailable",
      url: import.meta.env.VITE_PREDICTION_API_URL || "http://localhost:8000",
    };
  }
}
